import { contactOrderTargetEvents } from "../../../_logic/create-result"
import type {
  BuildContactOrderCreateIntentInput,
  ContactOrderCreateIntent,
} from "../create-contact-orders-contract"

const blockedIntent = ({
  label,
  pendingCount,
  disabledReason,
  message = disabledReason,
  hidden = false,
}: {
  label: string
  pendingCount: number
  disabledReason: string | null
  message?: string | null
  hidden?: boolean
}): ContactOrderCreateIntent => ({
  kind: "blocked",
  label,
  disabledReason,
  message,
  hidden,
  pendingCount,
})

export const buildContactOrderCreateIntent = ({
  loading,
  locked,
  pendingContactableCount,
  pendingContactOrders,
  appointmentRows,
  selectionState,
  allowedContactChannels,
  removedEventIds,
  allowRemoveOnlySubmission,
  buildPayloads,
  caseIdByOrderId,
  events,
  contactOrders,
  workOrders,
  label,
  successMessage,
}: BuildContactOrderCreateIntentInput): ContactOrderCreateIntent => {
  const pendingCount = pendingContactOrders.length
  const removedIds = new Set(removedEventIds)

  if (locked) {
    return blockedIntent({ label, pendingCount, disabledReason: null, message: null, hidden: true })
  }

  if (loading) {
    return blockedIntent({
      label,
      pendingCount,
      disabledReason: "Termine werden gerade geladen.",
    })
  }

  if (!pendingCount) {
    return blockedIntent({
      label,
      pendingCount,
      disabledReason: "Keine offenen Termine für neue Ankündigungspläne vorhanden.",
      message: events.length
        ? "Alle geplanten Termine haben bereits einen Ankündigungsplan."
        : "Keine Termine vorhanden.",
    })
  }

  if (
    allowRemoveOnlySubmission &&
    removedIds.size > 0 &&
    pendingContactOrders.every((event) => removedIds.has(event.id))
  ) {
    return {
      kind: "remove_only",
      label,
      disabledReason: null,
      message: null,
      hidden: false,
      pendingCount,
      removedEventIds: [...removedIds],
    }
  }

  if (!pendingContactableCount) {
    return blockedIntent({
      label,
      pendingCount,
      disabledReason: "Für die offenen Termine ist kein aktiver Kontaktweg ausgewählt.",
      message: "Für die offenen Termine ist kein Ankündigungsplan aktiv.",
    })
  }

  const targetEvents = contactOrderTargetEvents({
    pendingContactOrders,
    appointmentRows,
    selectionState,
    allowedContactChannels,
    includeAutomaticRows: true,
  }).filter((event) => !removedIds.has(event.id))

  if (!targetEvents.length) {
    return blockedIntent({
      label,
      pendingCount,
      disabledReason: "Für die offenen Termine ist kein aktiver Kontaktweg ausgewählt.",
      message: "Für die offenen Termine ist kein Ankündigungsplan aktiv.",
    })
  }

  const payloads = buildPayloads({
    targetEvents,
    appointmentRows,
    caseIdByOrderId,
    communicationSelectionByEventId: selectionState.communicationSelectionByEventId,
    allowedContactChannels,
  })

  if (!payloads.length) {
    return blockedIntent({
      label,
      pendingCount,
      disabledReason: "Für die offenen Termine ist kein aktiver Kontaktweg ausgewählt.",
      message: "Für die offenen Termine ist kein Ankündigungsplan aktiv.",
    })
  }

  return {
    kind: "commit",
    label,
    disabledReason: null,
    message: null,
    hidden: false,
    pendingCount,
    command: {
      payloads,
      targetEvents: [...targetEvents],
      events: [...events],
      contactOrders: [...contactOrders],
    },
    workOrders: [...workOrders],
    successMessage,
    removedEventIds: [...removedIds],
  }
}
